import wikiData from '@/data/wikiLoadoutData'
import gameIcons from '@/data/gameIconManifest.json'
import survivorBuildDefaults from '@/data/survivorBuildDefaults'
import { isStrictlyDominatedOffering } from '@/data/offeringKnowledge'
import generateSurvivorBuild from '@/lib/buildGenerator/generateSurvivorBuild'
import {
  canonicalSignals,
  createLoadoutContext,
  decorateCorrelation,
  isEventPoolEntry,
  isGameplayOffering,
  scoreAddonCorrelation,
  scoreAddonPair,
  scoreItemCorrelation,
  scoreOfferingCorrelation
} from '@/lib/loadoutCorrelation'
import {
  equipmentPackageCorrelation,
  selectEquipmentPackage
} from '@/lib/equipmentPackageEngine'
import {
  KILLER_PERK_STRENGTH,
  PERK_ROLE_RULES,
  getKillerProfile,
  itemCategoryName,
  normalizeGameName,
  rarityLabel
} from '@/data/analysisProfiles'
import {
  KILLER_BUILD_ARCHETYPES,
  areKillerGameplayEquivalents,
  getKillerCatalogPerkId,
  getKillerGameplayPerkId,
  getKillerPerkGameplayTypes,
  getKillerPerkPairRule
} from '@/data/killerPerkKnowledge'

function randomItem (items, random) {
  if (!items || !items.length) return null
  return items[Math.floor(random() * items.length)]
}

function randomItemExcluding (items, excludedIds, random) {
  const excluded = new Set(excludedIds || [])
  const available = (items || []).filter(item => !excluded.has(item.id))
  return randomItem(available.length ? available : items, random)
}

const SURVIVOR_PLAN_DEFINITIONS = [
  {
    id: 'chase_escape',
    signals: ['chase'],
    supportingSignals: ['stealth', 'information'],
    keywords: ['chase', 'pallet', 'window', 'vault', 'exhaustion', 'haste', 'stun'],
    label: 'Chase and escape',
    summary: 'Read the route, extend the chase and convert a clean escape window.'
  },
  {
    id: 'healing_support',
    signals: ['healing'],
    supportingSignals: ['hook', 'information'],
    keywords: ['heal', 'altruism', 'injured', 'rescue', 'unhook'],
    label: 'Healing support',
    summary: 'Reach injured teammates, accelerate recovery and protect the reset.'
  },
  {
    id: 'generator_pressure',
    signals: ['generator'],
    supportingSignals: ['information', 'item'],
    keywords: ['gen', 'generator', 'repair', 'skillcheck', 'objective', 'progression'],
    label: 'Generator pressure',
    summary: 'Find productive generators and turn repair windows into reliable progress.'
  },
  {
    id: 'stealth_evasion',
    signals: ['stealth'],
    supportingSignals: ['chase', 'information'],
    keywords: ['stealth', 'scratchmark', 'quiet', 'disappear', 'locker', 'occlusion'],
    label: 'Stealth and evasion',
    summary: 'Break tracking, hide the transition and make the killer lose the route.'
  },
  {
    id: 'aura_information',
    signals: ['information'],
    supportingSignals: ['generator', 'chase', 'totem'],
    keywords: ['aura', 'tracking', 'reveal', 'notification', 'vision'],
    label: 'Information network',
    summary: 'Collect reliable information and turn it into safer pathing and decisions.'
  },
  {
    id: 'rescue_teamplay',
    signals: ['hook'],
    supportingSignals: ['healing', 'information'],
    keywords: ['rescue', 'unhook', 'hook', 'teamplay', 'protect', 'save'],
    label: 'Rescue teamplay',
    summary: 'Coordinate the save, secure the reset and keep the team moving.'
  },
  {
    id: 'item_economy',
    signals: ['item', 'loot'],
    supportingSignals: ['information', 'generator', 'healing'],
    keywords: ['item', 'charge', 'chest', 'loot', 'rummage', 'resource'],
    label: 'Item economy',
    summary: 'Create, preserve and convert item charges into sustained match value.'
  },
  {
    id: 'totem_control',
    signals: ['totem'],
    supportingSignals: ['information', 'healing'],
    keywords: ['totem', 'hex', 'boon', 'cleanse', 'bless'],
    label: 'Totem control',
    summary: 'Locate totems quickly and convert them into information, safety or healing.'
  },
  {
    id: 'endgame_survival',
    signals: ['endgame'],
    supportingSignals: ['obsession', 'stealth', 'chase'],
    keywords: ['endgame', 'exitgate', 'hatch', 'survival', 'obsession'],
    label: 'Endgame survival',
    summary: 'Preserve resources for the final phase and create a dependable escape route.'
  },
  {
    id: 'sabotage_save',
    signals: ['sabotage'],
    supportingSignals: ['hook', 'chase'],
    keywords: ['sabotage', 'wiggle', 'carrying', 'pickup', 'pickupdenial', 'hookdenial'],
    label: 'Sabotage rescue',
    summary: 'Read the pickup, deny the hook and convert carried time into a rescue.'
  },
  {
    id: 'slug_recovery',
    signals: ['healing'],
    supportingSignals: ['hook', 'chase'],
    keywords: ['dying', 'recovery', 'recover', 'crawl', 'ground', 'slug', 'antislug', 'pickupdenial', 'unbreakable'],
    label: 'Ground recovery',
    summary: 'Recover under pressure, reposition while downed and punish unattended slugs.'
  },
  {
    id: 'protection_survival',
    signals: ['hook', 'healing'],
    supportingSignals: ['chase', 'obsession'],
    keywords: ['antitunnel', 'endurance', 'protection', 'bodyblock', 'deepwound', 'decisive'],
    label: 'Protection and survival',
    summary: 'Absorb pressure, survive the tunnel and convert protection into team tempo.'
  },
  {
    id: 'linked_teamwork',
    signals: ['hook', 'healing', 'information'],
    supportingSignals: ['generator', 'chase'],
    keywords: ['teamwork', 'teamplay', 'linked', 'partner', 'cooperative', 'coop', 'one_two_three_four'],
    label: 'Linked teamwork',
    summary: 'Stay linked to a teammate and turn coordinated actions into shared momentum.'
  }
]

const SURVIVOR_ARCHETYPE_PLAN_HINTS = {
  chase: 'chase_escape',
  pallet: 'chase_escape',
  vault: 'chase_escape',
  exhaustion: 'chase_escape',
  heal: 'healing_support',
  altruism: 'healing_support',
  generator: 'generator_pressure',
  repair: 'generator_pressure',
  stealth: 'stealth_evasion',
  scratch: 'stealth_evasion',
  aura: 'aura_information',
  information: 'aura_information',
  unhook: 'rescue_teamplay',
  rescue: 'rescue_teamplay',
  item: 'item_economy',
  chest: 'item_economy',
  loot: 'item_economy',
  totem: 'totem_control',
  boon: 'totem_control',
  endgame: 'endgame_survival',
  hatch: 'endgame_survival',
  sabotage: 'sabotage_save',
  denial: 'sabotage_save',
  wiggle: 'sabotage_save',
  dying: 'slug_recovery',
  recovery: 'slug_recovery',
  crawl: 'slug_recovery',
  slug: 'slug_recovery',
  antitunnel: 'protection_survival',
  endurance: 'protection_survival',
  protection: 'protection_survival',
  bodyblock: 'protection_survival',
  teamwork: 'linked_teamwork',
  linked: 'linked_teamwork',
  partner: 'linked_teamwork'
}

function normalizePlanText (value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function planDefinitionById (planId) {
  return SURVIVOR_PLAN_DEFINITIONS.find(definition => definition.id === planId) || null
}

function survivorPlanSignals (perk) {
  return canonicalSignals((perk.tags || []).concat(perk.roles || [], perk.primaryRole || []))
}

function survivorPlanKeywords (perk) {
  return (perk.tags || []).concat(perk.roles || [], perk.primaryRole || []).map(normalizePlanText)
}

function survivorPlanFit (definition, perks) {
  const perkFits = perks.map(perk => {
    const signals = survivorPlanSignals(perk)
    const keywords = survivorPlanKeywords(perk)
    const primaryMatches = definition.signals.filter(signal => signals.indexOf(signal) !== -1)
    const supportingMatches = definition.supportingSignals.filter(signal => signals.indexOf(signal) !== -1)
    const keywordMatches = definition.keywords.filter(keyword => keywords.some(tag => tag.indexOf(keyword) !== -1))
    return {
      matched: primaryMatches.length > 0 || keywordMatches.length > 0,
      score: primaryMatches.length * 14 + supportingMatches.length * 2 + Math.min(10, keywordMatches.length * 2)
    }
  })
  return {
    coverage: perkFits.filter(fit => fit.matched).length,
    score: perkFits.reduce((sum, fit) => sum + fit.score, 0)
  }
}

function inferSurvivorBuildPlan (perks, survivorBuild) {
  const comboDebug = survivorBuild && survivorBuild.scoring && survivorBuild.scoring.comboDebug
  const centralAudit = comboDebug && comboDebug.centralProfileAudit
  const archetype = (centralAudit && centralAudit.archetype) || ''
  const archetypeText = normalizePlanText(archetype)
  const ranked = SURVIVOR_PLAN_DEFINITIONS.map(definition => {
    const fit = survivorPlanFit(definition, perks)
    const hinted = Object.keys(SURVIVOR_ARCHETYPE_PLAN_HINTS).some(keyword => (
      archetypeText.indexOf(keyword) !== -1 && SURVIVOR_ARCHETYPE_PLAN_HINTS[keyword] === definition.id
    ))
    return Object.assign({}, definition, {
      coverage: fit.coverage,
      score: fit.score + fit.coverage * 6 + (hinted ? 18 : 0)
    })
  }).sort((first, second) => second.score - first.score || second.coverage - first.coverage)
  const primary = ranked[0] || SURVIVOR_PLAN_DEFINITIONS[0]
  const secondary = ranked.find(definition => definition.id !== primary.id && definition.coverage >= 2) || ranked[1] || null
  const roleCoverage = comboDebug && comboDebug.roleSlotCoverage
  const coveredRoles = roleCoverage ? roleCoverage.coveredSlots.length : Math.min(3, Math.max(0, primary.coverage - 1))
  const totalRoles = roleCoverage
    ? roleCoverage.coveredSlots.length + roleCoverage.missingSlots.length
    : 3
  const uniqueSignals = Array.from(new Set(perks.reduce((signals, perk) => signals.concat(survivorPlanSignals(perk)), [])))
  const quality = comboDebug
    ? (comboDebug.comboQualityLabel || 'Partial').toLowerCase().replace(/[^a-z]+/g, '_')
    : primary.coverage >= 3 ? 'specified' : 'partial'
  return {
    id: primary.id,
    primary: primary.id,
    secondary: secondary ? secondary.id : null,
    primarySignals: primary.signals.slice(),
    supportingSignals: primary.supportingSignals.slice(),
    secondarySignals: secondary ? secondary.signals.slice() : [],
    secondarySupportingSignals: secondary ? secondary.supportingSignals.slice() : [],
    signals: uniqueSignals,
    coverage: primary.coverage,
    coverageTotal: perks.length,
    roleCoverage: {
      covered: coveredRoles,
      total: totalRoles,
      missing: roleCoverage ? roleCoverage.missingSlots.slice() : []
    },
    quality,
    centralArchetype: archetype || null,
    comboCandidates: comboDebug ? comboDebug.comboCandidateCount : null,
    qaFlags: comboDebug ? comboDebug.qaFlags.slice() : []
  }
}

function survivorProfileFromPlan (plan) {
  const definition = planDefinitionById(plan && plan.primary) || SURVIVOR_PLAN_DEFINITIONS[0]
  return {
    id: definition.id,
    label: definition.label,
    accent: '#64dcff',
    tags: definition.signals.concat(definition.supportingSignals),
    summary: definition.summary
  }
}

function tagOverlap (first, second) {
  const target = new Set((second || []).map(tag => String(tag).toLowerCase()))
  return (first || []).filter(tag => target.has(String(tag).toLowerCase())).length
}

function killerPairEvidence (perk, selectedPerks) {
  const rules = (selectedPerks || [])
    .filter(selectedPerk => selectedPerk && !areKillerGameplayEquivalents(perk, selectedPerk))
    .map(selectedPerk => {
      const pairRule = getKillerPerkPairRule(perk, selectedPerk)
      return pairRule ? Object.assign({ withPerk: selectedPerk }, pairRule) : null
    })
    .filter(Boolean)
  const blocked = rules.filter(rule => rule.blocked)
  const drawbacks = rules
    .filter(rule => rule.kind !== 'synergy')
    .sort((first, second) => first.weight - second.weight)
  const synergies = rules
    .filter(rule => rule.kind === 'synergy')
    .sort((first, second) => second.weight - first.weight)
  return {
    primary: blocked[0] || synergies[0] || drawbacks[0] || null,
    synergy: synergies[0] || null,
    drawback: blocked[0] || drawbacks[0] || null
  }
}

function killerPerkCompatibility (perk, centralPerk, profile, intentRole, relationship, archetype, selectedPerks) {
  if (relationship === 'central') {
    return {
      score: 100,
      kind: 'central',
      confidence: 'curated',
      confidenceLabel: 'Power-profile anchor',
      reason: `Selected as the anchor for the ${archetype ? archetype.title : 'killer power'} game plan.`
    }
  }
  const duplicate = (selectedPerks || []).find(selectedPerk => areKillerGameplayEquivalents(perk, selectedPerk))
  if (duplicate) {
    return {
      score: 0,
      kind: 'duplicate',
      blocked: true,
      confidence: 'exact',
      confidenceLabel: 'Gameplay identity',
      reason: `${perk.displayName} and ${duplicate.displayName} are two names for the same gameplay effect.`,
      advantages: [],
      drawbacks: ['Duplicate gameplay effect.']
    }
  }
  const pairEvidence = killerPairEvidence(perk, selectedPerks && selectedPerks.length ? selectedPerks : [centralPerk])
  const pairRule = pairEvidence.primary
  if (pairRule) {
    return {
      score: pairRule.weight,
      kind: pairRule.kind,
      blocked: Boolean(pairRule.blocked),
      confidence: 'exact',
      confidenceLabel: 'Mechanic contract',
      reason: pairRule.reason,
      advantages: pairEvidence.synergy ? ['Verified perk-to-perk mechanic.'] : [],
      drawbacks: pairEvidence.drawback ? [pairEvidence.drawback.reason] : []
    }
  }
  const sharedTags = tagOverlap(perk.tags, centralPerk && centralPerk.tags)
  const profileMatches = tagOverlap(perk.tags, profile.tags)
  const roleMatch = perk.primaryRole === intentRole
  const sameCentralRole = centralPerk && perk.primaryRole === centralPerk.primaryRole
  const archetypeMatches = archetype
    ? perk.gameplayTypes.filter(type => archetype.preferredTypes.indexOf(type) !== -1).length
    : 0
  const score = Math.min(100, 30 + sharedTags * 5 + profileMatches * 5 + archetypeMatches * 12 + (roleMatch ? 12 : 0) - (sameCentralRole ? 8 : 0))
  const confidence = profileMatches > 0 || roleMatch || archetypeMatches > 0 ? 'profiled' : 'inferred'
  const reason = archetypeMatches > 0
    ? `This perk covers ${perk.gameplayTypes.filter(type => archetype.preferredTypes.indexOf(type) !== -1).slice(0, 2).join(' / ')} for the ${archetype.title} plan.`
    : roleMatch
      ? `This perk fills the intended ${intentRole} job for this slot.`
      : sharedTags > 0
        ? `${sharedTags} shared tag${sharedTags > 1 ? 's' : ''} support the same theme, not a guaranteed mechanic combo.`
        : `The fit is inferred from the ${profile.id} killer profile rather than an exact perk interaction.`
  return {
    score,
    kind: sharedTags > 0 ? 'theme' : 'coverage',
    confidence,
    confidenceLabel: confidence === 'profiled' ? 'Archetype-supported' : 'Inferred fit',
    reason,
    advantages: [archetypeMatches > 0 ? `Supports ${archetype.title}.` : roleMatch ? `Covers the ${intentRole} role.` : `Supports the ${profile.id} profile.`],
    drawbacks: sameCentralRole ? ['Possible role overlap with the central perk.'] : []
  }
}

function weightedChoice (entries, chaos, random) {
  if (!entries.length) return null
  const normalizedChaos = Math.max(0, Math.min(100, Number(chaos) || 0)) / 100
  if (normalizedChaos === 0) {
    return entries.slice().sort((first, second) => (second.score || 0) - (first.score || 0))[0]
  }
  if (normalizedChaos >= 0.96) return randomItem(entries, random)
  const exponent = 1.15 + (1 - normalizedChaos) * 2.8
  const weights = entries.map(entry => Math.pow(Math.max(1, entry.score || 1), exponent))
  const total = weights.reduce((sum, weight) => sum + weight, 0)
  let cursor = random() * total
  for (let index = 0; index < entries.length; index++) {
    cursor -= weights[index]
    if (cursor <= 0) return entries[index]
  }
  return entries[entries.length - 1]
}

function optimizedBandChoice (entries, chaos, random, scoreBand) {
  if (!entries.length) return null
  const normalizedChaos = Math.max(0, Math.min(100, Number(chaos) || 0))
  if (normalizedChaos !== 0) return weightedChoice(entries, chaos, random)
  const bestScore = Math.max(...entries.map(entry => entry.score || 0))
  const candidates = entries.filter(entry => (entry.score || 0) >= bestScore - (scoreBand || 0))
  return randomItem(candidates.length ? candidates : entries, random)
}

function perkRoles (tags) {
  const normalizedTags = (tags || []).map(tag => String(tag).toLowerCase())
  return PERK_ROLE_RULES
    .filter(rule => rule.keywords.some(keyword => normalizedTags.some(tag => tag === keyword || tag.indexOf(keyword) === 0)))
    .map(rule => rule.role)
}

function primaryKillerRole (tags, roles, gameplayTypes) {
  const normalized = (tags || []).map(tag => String(tag).toLowerCase())
  const types = gameplayTypes || []
  const has = keywords => keywords.some(keyword => normalized.some(tag => tag === keyword || tag.indexOf(keyword) === 0))
  if (types.indexOf('generator_slowdown') !== -1 || has(['regression', 'progressionpenalty', 'regressionspeed', 'genblocker', 'repairingproficiency'])) return 'regression'
  if (types.indexOf('hex') !== -1) return 'hex'
  if (types.indexOf('endgame') !== -1) return 'endgame'
  if (has(['aura', 'scream', 'notification', 'killerinstinct', 'reveal'])) return 'information'
  if (types.indexOf('chase_loop') !== -1 || types.indexOf('chase_conversion') !== -1 || types.indexOf('exposed') !== -1 || has(['haste', 'hindered', 'pallet', 'window', 'vault', 'lunge', 'exposed', 'basicattack', 'attackcooldown', 'stunrecovery'])) return 'chase'
  if (types.indexOf('anti_heal') !== -1) return 'anti_heal'
  if (has(['hook', 'dying', 'carrying', 'injured', 'healing'])) return 'snowball'
  if (types.indexOf('stealth') !== -1 || has(['undetectable', 'oblivious', 'stealth', 'deception'])) return 'stealth'
  if (types.indexOf('terror_radius') !== -1) return 'control'
  if (types.indexOf('obsession') !== -1) return 'obsession'
  if (types.indexOf('hex_support') !== -1) return 'control'
  if (has(['locker', 'reload'])) return 'reload'
  return roles[0] || 'control'
}

function createKillerPerkEntry (data) {
  const tags = (data.metadata && data.metadata.tags) || []
  const gameplayId = getKillerGameplayPerkId(data.id)
  const gameplayTypes = getKillerPerkGameplayTypes({ id: data.id, gameplayId, tags })
  const roles = uniqueValues(perkRoles(tags).concat(gameplayTypes))
  return {
    index: data.index,
    id: data.id,
    gameplayId,
    name: data.name,
    spriteKey: data.spriteKey,
    hasSprite: data.hasSprite,
    displayName: data.displayName,
    tags,
    patch: data.metadata && data.metadata.patch,
    values: (data.metadata && data.metadata.values) || [],
    units: (data.metadata && data.metadata.units) || [],
    mechanics: (data.metadata && data.metadata.mechanics) || {},
    roles,
    gameplayTypes,
    primaryRole: primaryKillerRole(tags, roles, gameplayTypes),
    strengthScore: KILLER_PERK_STRENGTH[gameplayId] || KILLER_PERK_STRENGTH[data.id] || 52
  }
}

function findWikiPerk (name, side) {
  const entries = side === 'killer' ? wikiData.killerPerks : wikiData.survivorPerks
  const normalized = normalizeGameName(name)
  const compact = normalized.replace(/_/g, '').replace(/chilli/g, 'chili')
  return entries.find(perk => {
    const perkName = normalizeGameName(perk.name)
    return perkName === normalized || perkName.replace(/_/g, '').replace(/chilli/g, 'chili') === compact
  }) || null
}

export function createKillerCatalog (frameNames, getDisplayName) {
  const spriteCatalog = frameNames.map((frameName, index) => {
    const displayName = getDisplayName(frameName)
    const canonicalFrameName = frameName
      .replace(/^\d+_/, '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    const frameId = getKillerCatalogPerkId(normalizeGameName(canonicalFrameName))
    const metadata = findWikiPerk(displayName, 'killer') ||
      findWikiPerk(canonicalFrameName, 'killer') ||
      wikiData.killerPerks.find(perk => perk.id === frameId) || null
    const id = getKillerCatalogPerkId(metadata ? metadata.id : frameId)
    return createKillerPerkEntry({
      index,
      id,
      name: frameName,
      spriteKey: frameName,
      hasSprite: true,
      displayName,
      metadata
    })
  })
  const catalog = []
  const knownIds = new Set()
  spriteCatalog.forEach(perk => {
    if (knownIds.has(perk.id)) return
    knownIds.add(perk.id)
    catalog.push(perk)
  })
  const wikiOnly = wikiData.killerPerks.filter(perk => !knownIds.has(perk.id)).map((metadata, offset) => {
    return createKillerPerkEntry({
      index: catalog.length + offset,
      id: metadata.id,
      name: metadata.id,
      spriteKey: null,
      hasSprite: false,
      displayName: metadata.name,
      metadata
    })
  })
  return catalog.concat(wikiOnly)
}

function killerPerkScore (perk, profile, selected) {
  const profileMatch = tagOverlap(perk.tags, profile.tags)
  const preferredRoleMatch = perk.roles.filter(role => profile.perkRoles.indexOf(role) !== -1).length
  const repeatedRoles = perk.roles.reduce((count, role) => {
    return count + selected.filter(selectedPerk => selectedPerk.roles.indexOf(role) !== -1).length
  }, 0)
  return perk.strengthScore + profileMatch * 14 + preferredRoleMatch * 11 - repeatedRoles * 9
}

const KILLER_CHAOS_ZERO_POOL_LIMIT = 16
const KILLER_CHAOS_ZERO_ROLE_POOL = 2

function killerComboRoleCounts (perks) {
  return perks.reduce((counts, perk) => {
    counts[perk.primaryRole] = (counts[perk.primaryRole] || 0) + 1
    return counts
  }, {})
}

function scoreKillerCombo (perks, profile) {
  const roleCounts = killerComboRoleCounts(perks)
  const roles = Object.keys(roleCounts)
  const baseValue = perks.reduce((sum, perk) => sum + killerPerkScore(perk, profile, []), 0)
  const repeatedRolePenalty = roles.reduce((sum, role) => {
    const repeated = Math.max(0, roleCounts[role] - 1)
    return sum + repeated * repeated * 24
  }, 0)
  const profileRoleCoverage = profile.perkRoles.filter(role => roleCounts[role]).length
  const hasInformation = Boolean(roleCounts.information)
  const hasPressure = Boolean(roleCounts.regression || roleCounts.control)
  const hasLethality = Boolean(roleCounts.chase || roleCounts.snowball || roleCounts.stealth)
  const phaseCoverage = [hasInformation, hasPressure, hasLethality].filter(Boolean).length
  let pairwiseBridge = 0
  for (let first = 0; first < perks.length; first += 1) {
    for (let second = first + 1; second < perks.length; second += 1) {
      pairwiseBridge += Math.min(3, tagOverlap(perks[first].tags, perks[second].tags)) * 2
    }
  }
  return baseValue + roles.length * 16 + profileRoleCoverage * 18 + phaseCoverage * 22 + Math.min(24, pairwiseBridge) - repeatedRolePenalty
}

function globalKillerChaosZeroPerks (catalog, profile, selected, excluded) {
  const forced = selected.filter(Boolean)
  const missingSlots = selected.reduce((slots, perk, index) => {
    if (!perk) slots.push(index)
    return slots
  }, [])
  if (!missingSlots.length) return selected

  const available = catalog.filter(perk => !excluded.has(perk.id) && !forced.some(choice => areKillerGameplayEquivalents(choice, perk)))
  const byId = new Map()
  available
    .slice()
    .sort((first, second) => killerPerkScore(second, profile, forced) - killerPerkScore(first, profile, forced))
    .slice(0, KILLER_CHAOS_ZERO_POOL_LIMIT)
    .forEach(perk => byId.set(perk.gameplayId, perk))
  const desiredRoles = Array.from(new Set(profile.perkRoles.concat(['information', 'regression', 'control', 'chase', 'snowball', 'stealth'])))
  desiredRoles.forEach(role => {
    available
      .filter(perk => perk.primaryRole === role)
      .sort((first, second) => killerPerkScore(second, profile, forced) - killerPerkScore(first, profile, forced))
      .slice(0, KILLER_CHAOS_ZERO_ROLE_POOL)
      .forEach(perk => byId.set(perk.gameplayId, perk))
  })
  const candidates = Array.from(byId.values())
  let bestCombo = null
  let bestScore = -Infinity

  function visit (startIndex, chosen) {
    if (chosen.length === missingSlots.length) {
      const complete = forced.concat(chosen)
      const score = scoreKillerCombo(complete, profile)
      if (score > bestScore) {
        bestScore = score
        bestCombo = chosen.slice()
      }
      return
    }
    const remaining = missingSlots.length - chosen.length
    for (let index = startIndex; index <= candidates.length - remaining; index += 1) {
      visit(index + 1, chosen.concat(candidates[index]))
    }
  }

  visit(0, [])
  if (!bestCombo) return selected
  bestCombo
    .sort((first, second) => killerPerkScore(second, profile, forced) - killerPerkScore(first, profile, forced))
    .forEach((perk, index) => { selected[missingSlots[index]] = perk })
  return selected
}

function selectKillerPerks (catalog, killer, chaos, random, lockedPerks, excludedPerkIds) {
  const profile = getKillerProfile(killer)
  const excluded = new Set(excludedPerkIds || [])
  const selected = new Array(4).fill(null)
  const requestedLocks = lockedPerks || []
  requestedLocks.slice(0, 4).forEach((perk, index) => {
    if (perk) selected[index] = perk
  })
  if (Number(chaos || 0) === 0) {
    return globalKillerChaosZeroPerks(catalog, profile, selected, excluded).filter(Boolean)
  }
  for (let slot = 0; slot < selected.length; slot++) {
    if (selected[slot]) continue
    const chosen = selected.filter(Boolean)
    const pool = catalog
      .filter(perk => !excluded.has(perk.id) && !chosen.some(selectedPerk => areKillerGameplayEquivalents(selectedPerk, perk)))
      .map(perk => ({ perk, score: killerPerkScore(perk, profile, chosen) }))
    const choice = weightedChoice(pool, chaos, random)
    if (!choice) break
    selected[slot] = choice.perk
  }
  return selected.filter(Boolean)
}

function compatibleOfferings (side) {
  const charType = side === 'killer' ? 'K' : 'S'
  return wikiData.offerings.filter(offering => {
    return (!offering.charType || offering.charType === charType) && gameIcons.offerings[String(offering.id)] && isGameplayOffering(offering)
  })
}

function selectAddons (pool, context, chaos, random, lockedAddons, excludedAddonIds) {
  const excluded = new Set(excludedAddonIds || [])
  const available = pool.filter(addon => {
    return !excluded.has(addon.id) && !(addon.mechanics && addon.mechanics.notAvailable)
  })
  const locked = (lockedAddons || []).slice(0, 2)
  const pairCandidates = []

  if (locked[0] && locked[1]) {
    pairCandidates.push([locked[0], locked[1]])
  } else if (locked[0] || locked[1]) {
    const lockedIndex = locked[0] ? 0 : 1
    const lockedAddon = locked[lockedIndex]
    available.filter(addon => addon.id !== lockedAddon.id).forEach(addon => {
      const pair = new Array(2)
      pair[lockedIndex] = lockedAddon
      pair[lockedIndex === 0 ? 1 : 0] = addon
      pairCandidates.push(pair)
    })
  } else {
    for (let first = 0; first < available.length; first += 1) {
      for (let second = first + 1; second < available.length; second += 1) {
        pairCandidates.push([available[first], available[second]])
      }
    }
  }

  if (!pairCandidates.length) {
    const only = locked.find(Boolean) || available[0]
    return only ? [decorateCorrelation(only, scoreAddonCorrelation(only, context, []))] : []
  }

  const scoredPairs = pairCandidates.map(addons => {
    const correlation = scoreAddonPair(addons[0], addons[1], context)
    return { addons, correlation, score: correlation.rawScore }
  })
  const curatedPairs = context.side === 'survivor' && Number(chaos || 0) === 0
    ? scoredPairs.filter(entry => entry.correlation.pairPlan && entry.correlation.pairPlanItemMatch !== false)
    : []
  let choice
  if (curatedPairs.length) {
    const bestCoverage = Math.max(...curatedPairs.map(entry => entry.correlation.coverage || 0))
    const mostRelevantPairs = curatedPairs.filter(entry => (entry.correlation.coverage || 0) === bestCoverage)
    const bestScore = Math.max(...mostRelevantPairs.map(entry => entry.score))
    const sTierPlans = mostRelevantPairs
      .filter(entry => entry.score >= bestScore - 18)
      .sort((first, second) => second.score - first.score)
      .slice(0, 4)
    choice = randomItem(sTierPlans.length ? sTierPlans : mostRelevantPairs, random)
  } else if (Number(chaos || 0) === 0) {
    const bestCoverage = Math.max(...scoredPairs.map(entry => entry.correlation.coverage || 0))
    const mostRelevantPairs = scoredPairs.filter(entry => (entry.correlation.coverage || 0) === bestCoverage)
    choice = optimizedBandChoice(mostRelevantPairs, chaos, random, 4)
  } else {
    choice = optimizedBandChoice(scoredPairs, chaos, random, 6)
  }
  if (!choice) return []
  return choice.addons.map((addon, index) => {
    const partner = choice.addons[index === 0 ? 1 : 0]
    return decorateCorrelation(addon, scoreAddonCorrelation(addon, context, [partner]))
  })
}
function selectOffering (pool, context, chaos, random) {
  const sourcePool = Number(chaos || 0) === 0
    ? (pool || []).filter(offering => !isStrictlyDominatedOffering(offering, pool))
    : (pool || [])
  const scoredOfferings = sourcePool.map(offering => {
    const correlation = scoreOfferingCorrelation(offering, context)
    return { offering, correlation, score: correlation.rawScore }
  })
  if (!scoredOfferings.length) return null
  if (Number(chaos || 0) !== 0) return weightedChoice(scoredOfferings, chaos, random)

  const bestCoverage = Math.max(...scoredOfferings.map(entry => entry.correlation.coverage || 0))
  const universalFallbackIds = context.side === 'killer'
    ? ['ebony_memento_mori', 'ivory_memento_mori']
    : ['shroud_of_union']
  const universalFallbacks = scoredOfferings.filter(entry => universalFallbackIds.indexOf(entry.offering.id) !== -1)
  const relevancePool = bestCoverage > 0
    ? scoredOfferings.filter(entry => (entry.correlation.coverage || 0) === bestCoverage)
    : universalFallbacks.length ? universalFallbacks : scoredOfferings
  const bestScore = Math.max(...relevancePool.map(entry => entry.score))
  const coherentBand = relevancePool.filter(entry => entry.score >= bestScore - 3)
  return randomItem(coherentBand.length ? coherentBand : relevancePool, random)
}

function findPreviousPerk (previous, slot, catalog) {
  if (!previous || !previous.perks || !previous.perks[slot]) return null
  return catalog.find(perk => perk.id === previous.perks[slot].id) || null
}

function lockedPerksFromPrevious (previous, locks, catalog) {
  return [0, 1, 2, 3].map(slot => {
    return locks[`perk-${slot}`] ? findPreviousPerk(previous, slot, catalog) : null
  })
}

function rollSurvivorBuild (catalog, chaos, random, previous, locks, excludedPerkIds) {
  const locked = lockedPerksFromPrevious(previous, locks, catalog)
  const config = Object.assign({}, survivorBuildDefaults, {
    chaos,
    blacklist: Array.from(new Set((survivorBuildDefaults.blacklist || []).concat(excludedPerkIds || []))),
    rerollPerkId: excludedPerkIds && excludedPerkIds.length ? excludedPerkIds[0] : null,
    lockedPerks: locked.map((perk, index) => perk ? {
      slot: index + 1,
      perkId: perk.id,
      influenceScoring: true
    } : null).filter(Boolean)
  })
  const build = generateSurvivorBuild(catalog, config, random)
  if (build.status === 'success') return build
  return {
    status: 'fallback',
    errors: build.errors || [],
    slots: catalog.slice(0, 4).map((perk, index) => ({ slot: index + 1, perk })),
    scoring: { chaos, comboDebug: null }
  }
}

function datasetMetrics () {
  const itemCount = wikiData.items.filter(item => gameIcons.items[String(item.id)]).length
  const addonCount = wikiData.survivorAddons.concat(wikiData.killerAddons).filter(addon => gameIcons.addons[String(addon.id)]).length
  const offeringCount = wikiData.offerings.filter(offering => gameIcons.offerings[String(offering.id)]).length
  const characterCount = wikiData.survivors.length + wikiData.killers.length
  const perkCount = wikiData.survivorPerks.length + wikiData.killerPerks.length
  return {
    characters: characterCount,
    perks: perkCount,
    addons: addonCount,
    offerings: offeringCount,
    total: characterCount + perkCount + itemCount + addonCount + offeringCount,
    generatedAt: wikiData.generatedAt
  }
}

function decorateEntry (entry, type) {
  if (!entry) return null
  const section = type === 'item'
    ? gameIcons.items
    : type === 'offering' ? gameIcons.offerings : gameIcons.addons
  return Object.assign({}, entry, {
    type,
    icon: section[String(entry.id)] || '',
    rarityLabel: rarityLabel(entry.rarity),
    categoryLabel: entry.category ? itemCategoryName(entry.category) : null
  })
}

export function rollCompleteLoadout (options) {
  const side = options.side === 'killer' ? 'killer' : 'survivor'
  const chaos = Number(options.chaos) || 0
  const random = options.random || Math.random
  const previous = options.previous || null
  const locks = options.locks || {}
  const forceDifferent = Boolean(options.rerollKey && previous)
  const characters = side === 'killer' ? wikiData.killers : wikiData.survivors
  const previousCharacter = previous && previous.character
  const lockedCharacter = locks.character && previousCharacter
    ? characters.find(character => character.id === previousCharacter.id)
    : null
  const excludedCharacterIds = forceDifferent && !locks.character && previousCharacter ? [previousCharacter.id] : []
  const character = lockedCharacter || randomItemExcluding(characters, excludedCharacterIds, random)
  let profile = side === 'killer' ? getKillerProfile(character) : null
  let item = null
  let equipmentPackage = null
  let addons = []

  const perkCatalog = side === 'killer' ? options.killerCatalog : options.survivorCatalog
  const lockedPerks = lockedPerksFromPrevious(previous, locks, perkCatalog)
  const excludedPerkIds = forceDifferent && previous && previous.perks
    ? previous.perks.filter((perk, index) => !locks[`perk-${index}`]).map(perk => perk.id)
    : []
  const survivorBuild = side === 'survivor'
    ? rollSurvivorBuild(perkCatalog, chaos, random, previous, locks, excludedPerkIds)
    : null
  const perks = side === 'killer'
    ? selectKillerPerks(perkCatalog, character, chaos, random, lockedPerks, excludedPerkIds)
    : survivorBuild.slots.map(slot => slot.perk)
  const plan = side === 'survivor' ? inferSurvivorBuildPlan(perks, survivorBuild) : null
  if (side === 'survivor') profile = survivorProfileFromPlan(plan)

  if (side === 'survivor') {
    const previousItem = previous && previous.item
    const previousAddons = previous && previous.addons ? previous.addons : []
    const lockedAddonSlots = [0, 1].map(index => (
      locks[`addon-${index}`] && previousAddons[index] ? previousAddons[index] : null
    ))
    const lockedAddonIds = lockedAddonSlots.filter(Boolean).map(addon => addon.id)
    const rerolledItem = forceDifferent && options.rerollKey === 'item'
    const rerolledAddonIndex = forceDifferent && /^addon-[01]$/.test(options.rerollKey)
      ? Number(options.rerollKey.split('-')[1])
      : -1
    const packageOptions = {
      perks,
      plan,
      chaos,
      random,
      itemId: locks.item && previousItem ? previousItem.id : null,
      lockedAddonIds,
      allowDominated: Boolean((locks.item && previousItem) || lockedAddonIds.length),
      excludedItemIds: rerolledItem && previousItem && !locks.item ? [previousItem.id] : [],
      excludedAddonIds: rerolledAddonIndex >= 0 && previousAddons[rerolledAddonIndex]
        ? [previousAddons[rerolledAddonIndex].id]
        : [],
      excludedPackageIds: []
    }
    const itemPackage = selectEquipmentPackage(Object.assign({}, packageOptions, {
      chaos: 0,
      excludedAddonIds: [],
      excludedPackageIds: []
    }))
    if (itemPackage) packageOptions.itemId = itemPackage.item.id
    equipmentPackage = selectEquipmentPackage(packageOptions)
    if (!equipmentPackage) {
      equipmentPackage = selectEquipmentPackage(Object.assign({}, packageOptions, {
        excludedItemIds: [],
        excludedAddonIds: [],
        excludedPackageIds: []
      }))
    }
    if (equipmentPackage) {
      item = equipmentPackage.item
      const remaining = equipmentPackage.addons.slice()
      addons = [0, 1].map(index => {
        const locked = lockedAddonSlots[index]
        if (!locked) return null
        const matchIndex = remaining.findIndex(addon => addon.id === locked.id)
        return matchIndex === -1 ? null : remaining.splice(matchIndex, 1)[0]
      })
      addons = addons.map(addon => addon || remaining.shift()).filter(Boolean)
      const packageCorrelation = equipmentPackageCorrelation(equipmentPackage)
      addons = addons.map(addon => decorateCorrelation(addon, packageCorrelation))
    }
  }

  const loadoutContext = createLoadoutContext({ side, profile, item, perks, plan })

  if (side === 'killer') {
    const addonPool = wikiData.killerAddons
      .filter(addon => addon.character === character.id && gameIcons.addons[String(addon.id)])
      .filter(addon => !(addon.mechanics && addon.mechanics.notAvailable))
      .filter(addon => !isEventPoolEntry(addon))
    const previousAddons = previous && previous.addons ? previous.addons : []
    const lockedAddons = [0, 1].map(index => {
      if (!locks[`addon-${index}`] || !previousAddons[index]) return null
      return addonPool.find(addon => addon.id === previousAddons[index].id) || null
    })
    const excludedAddonIds = forceDifferent
      ? previousAddons.filter((addon, index) => !locks[`addon-${index}`]).map(addon => addon.id)
      : []
    addons = selectAddons(addonPool, loadoutContext, chaos, random, lockedAddons, excludedAddonIds)
  }

  const offeringContext = createLoadoutContext({ side, profile, item, perks, addons, plan })
  const offeringPool = compatibleOfferings(side).filter(offering => !(offering.mechanics && offering.mechanics.notAvailable))
  const previousOffering = previous && previous.offering
  const lockedOffering = locks.offering && previousOffering
    ? offeringPool.find(offering => offering.id === previousOffering.id)
    : null
  const availableOfferings = forceDifferent && previousOffering
    ? offeringPool.filter(candidate => candidate.id !== previousOffering.id)
    : offeringPool
  const offeringChoice = selectOffering(availableOfferings, offeringContext, chaos, random)
  const offering = lockedOffering || (offeringChoice && offeringChoice.offering)
  const correlatedOffering = offering
    ? decorateCorrelation(offering, scoreOfferingCorrelation(offering, offeringContext))
    : null
  const correlatedItem = item
    ? decorateCorrelation(item, equipmentPackage
      ? equipmentPackageCorrelation(equipmentPackage)
      : scoreItemCorrelation(item, perks, plan))
    : null

  const averagePerkStrength = perks.length
    ? perks.reduce((sum, perk) => sum + (perk.strengthScore || perk.soloQueueScore || 55), 0) / perks.length
    : 50
  const correlationScores = addons.map(addon => addon.correlation.score)
    .concat(correlatedOffering ? [correlatedOffering.correlation.score] : [])
    .concat(correlatedItem ? [correlatedItem.correlation.score] : [])
  const averageCorrelation = correlationScores.length
    ? correlationScores.reduce((sum, score) => sum + score, 0) / correlationScores.length
    : 50
  const coherence = Math.max(12, Math.min(99, Math.round(
    34 + averageCorrelation * 0.58 - chaos * 0.28 + (averagePerkStrength - 50) * 0.14
  )))

  const decoratedCharacter = Object.assign({}, character, {
    icon: (gameIcons.characters[side] || {})[String(character.id)] || '',
    powerIcon: side === 'killer' ? (gameIcons.powers[String(character.id)] || '') : ''
  })

  return {
    side,
    chaos,
    character: decoratedCharacter,
    profile,
    plan,
    equipmentPackage: equipmentPackage ? {
      id: equipmentPackage.id,
      scenario: equipmentPackage.matchingScenario,
      strength: equipmentPackage.matchingStrength,
      tier: equipmentPackage.matchingTier,
      buildScore: equipmentPackage.buildScore,
      buildFit: equipmentPackage.buildFit,
      strategy: equipmentPackage.buildStrategy,
      playerDoctrine: equipmentPackage.matchingPlayerDoctrine,
      strategyAdjustment: equipmentPackage.strategyAdjustment,
      consistency: equipmentPackage.consistency,
      versatility: equipmentPackage.versatility,
      confidence: equipmentPackage.confidence,
      risks: equipmentPackage.risks,
      paretoOptimal: equipmentPackage.paretoOptimal,
      dominatedBy: equipmentPackage.dominatedBy,
      metrics: equipmentPackage.matchingMetrics
    } : null,
    item: decorateEntry(correlatedItem, 'item'),
    perks,
    addons: addons.map(addon => decorateEntry(addon, 'addon')),
    offering: decorateEntry(correlatedOffering, 'offering'),
    correlation: {
      score: Math.round(averageCorrelation),
      signals: loadoutContext.signals,
      itemProfile: loadoutContext.itemProfile ? loadoutContext.itemProfile.id : null
    },
    coherence,
    metrics: datasetMetrics(),
    rolledAt: Date.now()
  }
}

function uniqueValues (values) {
  return Array.from(new Set((values || []).filter(Boolean)))
}

function dedupeKillerEntriesByGameplayId (entries) {
  const seen = new Set()
  return (entries || []).filter(entry => {
    const perk = entry && (entry.perk || entry)
    if (!perk || seen.has(perk.gameplayId)) return false
    seen.add(perk.gameplayId)
    return true
  })
}

function perkHasAnyGameplayType (perk, types) {
  return (types || []).some(type => perk && perk.gameplayTypes.indexOf(type) !== -1)
}

function primaryRoleForGameplayType (type) {
  const aliases = {
    generator_slowdown: 'regression',
    generator_info: 'information',
    generator_trigger: 'control',
    aura: 'information',
    healing_info: 'information',
    tracking: 'information',
    chase_loop: 'chase',
    chase_conversion: 'chase',
    exposed: 'chase',
    basic_attack: 'chase',
    hook: 'snowball',
    scourge: 'snowball',
    hex_support: 'control',
    terror_radius: 'control',
    exhaustion_control: 'control'
  }
  return aliases[type] || type
}

function killerRolePlan (profile, strategy, archetype) {
  const strategyRoles = strategy === 'aggression'
    ? ['chase', 'information', 'snowball']
    : strategy === 'control'
      ? ['regression', 'control', 'information']
      : []
  const archetypeRoles = (archetype.slots || []).reduce((roles, slot) => {
    return roles.concat((slot.types || []).map(primaryRoleForGameplayType))
  }, [])
  return uniqueValues(archetypeRoles.concat(strategyRoles, profile.perkRoles))
}

function killerArchetypeCoverage (perks, archetype) {
  const slots = archetype.slots || []
  let bestAssignments = []

  function visit (slotIndex, usedIndexes, assignments) {
    if (slotIndex >= slots.length) {
      if (assignments.length > bestAssignments.length) bestAssignments = assignments.slice()
      return
    }
    visit(slotIndex + 1, usedIndexes, assignments)
    perks.forEach((perk, perkIndex) => {
      if (usedIndexes.has(perkIndex) || !perkHasAnyGameplayType(perk, slots[slotIndex].types)) return
      const nextUsed = new Set(usedIndexes)
      nextUsed.add(perkIndex)
      visit(slotIndex + 1, nextUsed, assignments.concat({ slot: slots[slotIndex], perk }))
    })
  }

  visit(0, new Set(), [])
  return {
    matched: bestAssignments.length,
    missing: Math.max(0, slots.length - bestAssignments.length),
    assignments: bestAssignments
  }
}

function killerBuildPerkScore (perk, profile, selected, centralPerk, mode, intentRole, relationship, archetype, includePairwiseMechanics = true) {
  let score = perk.strengthScore * 0.58 + tagOverlap(perk.tags, profile.tags) * 5
  if (relationship === 'central') {
    score += perkHasAnyGameplayType(perk, archetype.anchorTypes) ? 48 : -18
  }
  const repeatedPrimary = selected.filter(selectedPerk => selectedPerk.primaryRole === perk.primaryRole).length
  const repeatedRoles = perk.roles.reduce((count, role) => {
    return count + selected.filter(selectedPerk => selectedPerk.roles.indexOf(role) !== -1).length
  }, 0)
  const archetypeMatches = perk.gameplayTypes.filter(type => archetype.preferredTypes.indexOf(type) !== -1).length
  score += Math.min(1, archetypeMatches) * 18
  score -= repeatedPrimary * (mode === 'loadout_craft' ? 12 : 8)
  score -= Math.max(0, repeatedRoles - repeatedPrimary) * 1.5
  if (repeatedPrimary >= 2) score -= 18
  if (intentRole && perk.primaryRole === intentRole) score += mode === 'loadout_craft' ? 15 : 10
  if (profile.perkRoles.indexOf(perk.primaryRole) !== -1) score += 5
  if (centralPerk && !areKillerGameplayEquivalents(perk, centralPerk)) {
    score += tagOverlap(perk.tags, centralPerk.tags) * (mode === 'synergy_focus' ? 4 : 2)
  }
  if (includePairwiseMechanics) {
    selected.forEach(selectedPerk => {
      if (areKillerGameplayEquivalents(perk, selectedPerk)) {
        score -= 1000
        return
      }
      const pairRule = getKillerPerkPairRule(perk, selectedPerk)
      if (!pairRule) return
      if (pairRule.blocked) {
        score -= 1000
      } else if (pairRule.kind === 'synergy') {
        score += Math.max(0, pairRule.weight - 40) * (mode === 'synergy_focus' ? 0.72 : 0.5)
      } else {
        score -= Math.max(0, 65 - pairRule.weight) * 0.8
      }
    })
  }
  const selectedGeneratorPerks = selected.filter(selectedPerk => perkHasAnyGameplayType(selectedPerk, ['generator_slowdown'])).length
  if (perkHasAnyGameplayType(perk, ['generator_slowdown']) && selectedGeneratorPerks >= archetype.maxGeneratorPerks) score -= 140
  return score
}

function killerBuildFit (score) {
  return Math.max(24, Math.min(100, Math.round(20 + score * 0.72)))
}

function relationshipForSlot (slotIndex) {
  return ['central', 'synergy', 'power', 'coverage'][slotIndex] || 'coverage'
}

function desiredRoleForSlot (slotIndex, rolePlan, selected) {
  if (slotIndex === 1) return null
  const used = selected.filter(Boolean).map(perk => perk.primaryRole)
  return rolePlan.find(role => used.indexOf(role) === -1) || rolePlan[(slotIndex - 1) % rolePlan.length]
}

function rankedKillerChoices (catalog, profile, selected, centralPerk, config, slotIndex, rolePlan, archetype) {
  const relationship = relationshipForSlot(slotIndex)
  const intentRole = desiredRoleForSlot(slotIndex, rolePlan, selected)
  return catalog
    .filter(perk => !selected.some(choice => choice && areKillerGameplayEquivalents(choice, perk)))
    .map(perk => ({
      perk,
      score: killerBuildPerkScore(perk, profile, selected.filter(Boolean), centralPerk, config.mode, intentRole, relationship, archetype)
    }))
    .sort((first, second) => second.score - first.score || second.perk.strengthScore - first.perk.strengthScore)
}
const KILLER_BUILD_COMBO_POOL_LIMIT = 10
const KILLER_BUILD_ROLE_POOL = 1
const KILLER_BUILD_ARCHETYPE_POOL = 3
const KILLER_BUILD_TOTAL_POOL_LIMIT = 24

function scoreKillerBestBuildCombo (selection, profile, rolePlan, config, archetype) {
  const perks = selection.filter(Boolean)
  if (perks.length !== selection.length) return -Infinity
  for (let first = 0; first < perks.length; first += 1) {
    for (let second = first + 1; second < perks.length; second += 1) {
      if (areKillerGameplayEquivalents(perks[first], perks[second])) return -Infinity
      const pairRule = getKillerPerkPairRule(perks[first], perks[second])
      if (pairRule && pairRule.blocked) return -Infinity
    }
  }
  const roleCounts = killerComboRoleCounts(perks)
  const distinctRoles = Object.keys(roleCounts)
  const archetypeCoverage = killerArchetypeCoverage(perks, archetype)
  const slotValue = perks.reduce((total, perk, index) => {
    const otherPerks = perks.filter((choice, choiceIndex) => choiceIndex !== index)
    const intentRole = desiredRoleForSlot(index, rolePlan, perks.slice(0, index))
    return total + killerBuildPerkScore(
      perk,
      profile,
      otherPerks,
      perks[0],
      config.mode,
      intentRole,
      relationshipForSlot(index),
      archetype,
      false
    )
  }, 0)
  const profileCoverage = profile.perkRoles.filter(role => roleCounts[role]).length
  const repeatedPrimaryPenalty = distinctRoles.reduce((total, role) => {
    const repeated = Math.max(0, roleCounts[role] - 1)
    return total + repeated * repeated * 12
  }, 0)
  let pairwiseMechanics = 0
  for (let first = 0; first < perks.length; first += 1) {
    for (let second = first + 1; second < perks.length; second += 1) {
      const pairRule = getKillerPerkPairRule(perks[first], perks[second])
      if (!pairRule) continue
      pairwiseMechanics += pairRule.kind === 'synergy'
        ? Math.max(0, pairRule.weight - 40) * 0.45
        : -Math.max(0, 60 - pairRule.weight) * 0.7
    }
  }
  const generatorCount = perks.filter(perk => perkHasAnyGameplayType(perk, ['generator_slowdown'])).length
  const generatorOverflow = Math.max(0, generatorCount - archetype.maxGeneratorPerks)
  const preferredCoverage = new Set(perks.reduce((types, perk) => {
    return types.concat(perk.gameplayTypes.filter(type => archetype.preferredTypes.indexOf(type) !== -1))
  }, [])).size

  return slotValue +
    archetypeCoverage.matched * 82 -
    archetypeCoverage.missing * 190 +
    preferredCoverage * 18 +
    distinctRoles.length * 8 +
    profileCoverage * 6 +
    pairwiseMechanics -
    repeatedPrimaryPenalty -
    generatorOverflow * 220
}

function optimizeKillerBestBuildSelection (catalog, profile, rolePlan, selected, config, archetype) {
  const missingSlots = selected.reduce((slots, perk, index) => {
    if (!perk) slots.push(index)
    return slots
  }, [])
  if (!missingSlots.length) return selected

  const forced = selected.filter(Boolean)
  const available = catalog.filter(perk => !forced.some(choice => areKillerGameplayEquivalents(choice, perk)))
  const candidatesById = new Map()
  rankedKillerChoices(available, profile, selected, selected[0], config, missingSlots[0], rolePlan, archetype)
    .slice(0, KILLER_BUILD_COMBO_POOL_LIMIT)
    .forEach(entry => candidatesById.set(entry.perk.gameplayId, entry.perk))

  rolePlan.forEach(role => {
    available
      .filter(perk => perk.primaryRole === role)
      .sort((first, second) => {
        return killerBuildPerkScore(second, profile, forced, selected[0], config.mode, role, 'coverage', archetype) -
          killerBuildPerkScore(first, profile, forced, selected[0], config.mode, role, 'coverage', archetype)
      })
      .slice(0, KILLER_BUILD_ROLE_POOL)
      .forEach(perk => candidatesById.set(perk.gameplayId, perk))
  })
  archetype.slots.forEach(slot => {
    available
      .filter(perk => perkHasAnyGameplayType(perk, slot.types))
      .sort((first, second) => {
        return killerBuildPerkScore(second, profile, forced, selected[0], config.mode, null, 'coverage', archetype) -
          killerBuildPerkScore(first, profile, forced, selected[0], config.mode, null, 'coverage', archetype)
      })
      .slice(0, KILLER_BUILD_ARCHETYPE_POOL)
      .forEach(perk => candidatesById.set(perk.gameplayId, perk))
  })

  const candidates = Array.from(candidatesById.values())
    .sort((first, second) => {
      return killerBuildPerkScore(second, profile, forced, selected[0], config.mode, null, 'coverage', archetype) -
        killerBuildPerkScore(first, profile, forced, selected[0], config.mode, null, 'coverage', archetype)
    })
    .slice(0, KILLER_BUILD_TOTAL_POOL_LIMIT)
  const rankedSelections = []

  function visit (missingIndex, currentSelection, usedIds) {
    if (missingIndex >= missingSlots.length) {
      rankedSelections.push({
        selection: currentSelection.slice(),
        score: scoreKillerBestBuildCombo(currentSelection, profile, rolePlan, config, archetype),
        key: currentSelection.map(perk => perk.gameplayId).sort().join('|')
      })
      return
    }

    const slotIndex = missingSlots[missingIndex]
    candidates.forEach(perk => {
      if (usedIds.has(perk.gameplayId)) return
      const nextSelection = currentSelection.slice()
      const nextUsedIds = new Set(usedIds)
      nextSelection[slotIndex] = perk
      nextUsedIds.add(perk.gameplayId)
      visit(missingIndex + 1, nextSelection, nextUsedIds)
    })
  }

  visit(0, selected.slice(), new Set(forced.map(perk => perk.gameplayId)))
  rankedSelections.sort((first, second) => second.score - first.score || first.key.localeCompare(second.key))
  const bestBySet = new Map()
  rankedSelections.forEach(entry => {
    if (!bestBySet.has(entry.key)) bestBySet.set(entry.key, entry)
  })
  const best = Array.from(bestBySet.values())
    .sort((first, second) => second.score - first.score || first.key.localeCompare(second.key))[0]
  return best ? best.selection : selected
}

export const KILLER_BUILD_VARIANTS = KILLER_BUILD_ARCHETYPES

export function createKillerBestBuild (killer, catalog, options) {
  const config = Object.assign({
    strategy: 'balanced',
    mode: 'loadout_craft',
    centralPerkId: null,
    lockedSlots: {},
    variantIndex: 0
  }, options || {})
  const strategyTags = config.strategy === 'aggression'
    ? ['Pallets', 'Windows', 'Haste', 'Exposed', 'BasicAttacks']
    : config.strategy === 'control'
      ? ['Generators', 'Regression', 'Blocking', 'Hooks', 'Totems']
      : []
  const baseProfile = getKillerProfile(killer)
  const profile = Object.assign({}, baseProfile, { tags: uniqueValues(baseProfile.tags.concat(strategyTags)) })
  const selectedVariantIndex = Math.abs(Number(config.variantIndex) || 0) % KILLER_BUILD_VARIANTS.length
  const variant = Object.assign({ index: selectedVariantIndex }, KILLER_BUILD_VARIANTS[selectedVariantIndex])
  const rolePlan = killerRolePlan(profile, config.strategy, variant)
  const selected = new Array(4).fill(null)
  const centralRecommendations = dedupeKillerEntriesByGameplayId(catalog
    .slice()
    .map(perk => ({
      perk,
      score: killerBuildFit(killerBuildPerkScore(perk, profile, [], null, config.mode, profile.perkRoles[0], 'central', variant))
    }))
    .sort((first, second) => second.score - first.score || second.perk.strengthScore - first.perk.strengthScore))
    .slice(0, 5)
  const requestedCentralId = getKillerCatalogPerkId(config.centralPerkId)
  const requestedCentral = catalog.find(perk => perk.id === requestedCentralId)
  selected[0] = requestedCentral || (centralRecommendations[0] && centralRecommendations[0].perk) || null

  Object.keys(config.lockedSlots || {}).forEach(slotKey => {
    const slot = Number(slotKey)
    const lock = config.lockedSlots[slot]
    if (slot < 2 || slot > 4 || !lock || !lock.enabled) return
    const lockPerkId = getKillerCatalogPerkId(lock.perkId)
    const perk = catalog.find(candidate => candidate.id === lockPerkId)
    if (perk && !selected.some(choice => choice && areKillerGameplayEquivalents(choice, perk))) selected[slot - 1] = perk
  })

  optimizeKillerBestBuildSelection(catalog, profile, rolePlan, selected, config, variant)
    .forEach((perk, index) => {
      selected[index] = perk
    })

  const perks = selected.filter(Boolean)
  const archetypeCoverage = killerArchetypeCoverage(perks, variant)
  const slots = perks.map((perk, index) => {
    const otherPerks = perks.filter((choice, choiceIndex) => choiceIndex !== index)
    const plannedRelationship = relationshipForSlot(index)
    const intentRole = desiredRoleForSlot(index, rolePlan, perks.slice(0, index))
    const alternatives = index === 0 ? [] : dedupeKillerEntriesByGameplayId(catalog
      .filter(candidate => !otherPerks.some(choice => areKillerGameplayEquivalents(choice, candidate)) && !areKillerGameplayEquivalents(candidate, perk))
      .map(candidate => {
        const compatibility = killerPerkCompatibility(candidate, perks[0], profile, intentRole, plannedRelationship, variant, otherPerks)
        return {
          perk: candidate,
          score: killerBuildFit(killerBuildPerkScore(candidate, profile, otherPerks, perks[0], config.mode, intentRole, plannedRelationship, variant)),
          compatibility
        }
      })
      .filter(entry => !entry.compatibility.blocked)
      .sort((first, second) => second.score - first.score || second.perk.strengthScore - first.perk.strengthScore))
    const compatibility = killerPerkCompatibility(perk, perks[0], profile, intentRole, plannedRelationship, variant, otherPerks)
    const relationship = index === 0 ? 'central' : compatibility.kind === 'synergy' ? 'synergy' : plannedRelationship === 'synergy' ? 'coverage' : plannedRelationship
    const rawScore = killerBuildPerkScore(perk, profile, otherPerks, perks[0], config.mode, intentRole, relationship, variant)
    const archetypeAssignment = archetypeCoverage.assignments.find(assignment => areKillerGameplayEquivalents(assignment.perk, perk))
    return {
      slot: index + 1,
      perk,
      perkId: perk.id,
      score: killerBuildFit(rawScore),
      roles: perk.roles.slice(),
      primaryRole: perk.primaryRole,
      relationship,
      archetypeJob: archetypeAssignment ? archetypeAssignment.slot.id : null,
      sharedTags: index === 0 ? 0 : tagOverlap(perk.tags, perks[0].tags),
      profileMatches: tagOverlap(perk.tags, profile.tags),
      compatibility,
      alternatives
    }
  })
  const roleCounts = perks.reduce((counts, perk) => {
    counts[perk.primaryRole] = (counts[perk.primaryRole] || 0) + 1
    return counts
  }, {})
  const generatorCount = perks.filter(perk => perkHasAnyGameplayType(perk, ['generator_slowdown'])).length
  const warnings = Object.keys(roleCounts)
    .filter(role => roleCounts[role] >= 3)
    .map(role => ({ type: 'repeatedRole', role, count: roleCounts[role] }))
  if (archetypeCoverage.missing) warnings.push({ type: 'missingArchetypeSlots', count: archetypeCoverage.missing })
  if (generatorCount > variant.maxGeneratorPerks) warnings.push({ type: 'generatorOverflow', count: generatorCount })
  const centralOutsideArchetype = Boolean(requestedCentral && !perkHasAnyGameplayType(requestedCentral, variant.anchorTypes))
  if (centralOutsideArchetype) warnings.push({ type: 'centralOutsideArchetype', perkId: requestedCentral.id })
  const diversity = perks.length ? Math.round((Object.keys(roleCounts).length / perks.length) * 100) : 0
  const powerFit = slots.length
    ? Math.min(100, Math.round(slots.reduce((sum, slot) => sum + Math.min(100, 48 + slot.profileMatches * 18 + (profile.perkRoles.indexOf(slot.primaryRole) !== -1 ? 16 : 0)), 0) / slots.length))
    : 0
  const mechanicScores = []
  for (let first = 0; first < perks.length; first += 1) {
    for (let second = first + 1; second < perks.length; second += 1) {
      const pairRule = getKillerPerkPairRule(perks[first], perks[second])
      if (pairRule) mechanicScores.push(pairRule.weight)
    }
  }
  const synergy = mechanicScores.length
    ? Math.round(mechanicScores.reduce((sum, value) => sum + value, 0) / mechanicScores.length)
    : 45
  const strength = perks.length ? Math.round(perks.reduce((sum, perk) => sum + perk.strengthScore, 0) / perks.length) : 0
  const averageSlotFit = slots.length ? slots.reduce((sum, slot) => sum + slot.score, 0) / slots.length : 0
  const archetypeFit = Math.round((archetypeCoverage.matched / Math.max(1, variant.slots.length)) * 100)
  const guardrailPenalty = archetypeCoverage.missing * 12 + Math.max(0, generatorCount - variant.maxGeneratorPerks) * 15 + (centralOutsideArchetype ? 6 : 0)
  const score = Math.round(averageSlotFit * 0.55 + diversity * 0.12 + powerFit * 0.1 + archetypeFit * 0.23 - guardrailPenalty)
  return {
    killer,
    profile,
    variant,
    mode: config.mode,
    perks,
    slots,
    alternatives: slots.map(slot => slot.alternatives),
    centralRecommendations,
    score: Math.min(100, score),
    coverage: {
      roles: Object.keys(roleCounts),
      roleCounts,
      profileTags: profile.tags.slice(),
      rolePlan,
      archetype: variant.id,
      archetypeSlots: archetypeCoverage.assignments.map(assignment => assignment.slot.id),
      generatorCount
    },
    diagnostics: {
      centralFit: slots[0] ? slots[0].score : 0,
      powerFit,
      synergy,
      diversity,
      strength,
      archetypeFit
    },
    warnings
  }
}
export function getWikiMetrics () {
  return datasetMetrics()
}
